import { css, html, LitElement } from 'lit';
import { iconRemixArrowUpSLine as iconArrowUp } from '../../assets/cc-remix.icons.js';
import '../cc-icon/cc-icon.js';
import { CcToggleEvent } from '../common.events.js';

/**
 * A display component with mostly HTML+CSS and an open/close toggle feature organized with slots to display information such as CLI commands.
 * The main purpose is to be used with a cc-block.
 *
 * @cssdisplay block
 *
 * @slot button-text - The title of the tab. Try to only use text.
 * @slot link - A zone dedicated to link, for example to documentation.
 * @slot content - A zone dedicated to main content.
 */

export class CcBlockDetails extends LitElement {
  static get properties() {
    return {
      isOpen: { type: Boolean, reflect: true, attribute: 'is-open' },
    };
  }

  constructor() {
    super();

    /** @type {boolean} Sets the state of the toggle. */
    this.isOpen = false;
  }

  _onClickToggle() {
    this.isOpen = !this.isOpen;
    this.dispatchEvent(new CcToggleEvent({ isOpen: this.isOpen }));
  }

  render() {
    return html`
      <div class="wrapper">
        <div class="btn-wrapper">
          <button class="button" aria-expanded="${this.isOpen}" aria-controls="content" @click=${this._onClickToggle}>
            <slot name="button-text"></slot>
            <cc-icon .icon="${iconArrowUp}"></cc-icon>
          </button>
        </div>
        <div id="content" class="content">
          <slot name="content"></slot>
        </div>
        <div class="links">
          <slot name="link"></slot>
        </div>
      </div>
    `;
  }

  static get styles() {
    return [
      // language=CSS
      css`
        :host {
          container-type: inline-size;
          display: block;
        }

        .wrapper {
          align-items: center;
          display: grid;
          grid-template-areas: 'button links' 'content content';
          grid-template-columns: 1fr auto;
          justify-items: start;
        }

        @container (max-width: 34em) {
          .wrapper {
            grid-template-areas:
              'button'
              'content'
              'links';
            grid-template-columns: 100%;
          }
        }

        .btn-wrapper {
          border-radius: var(--cc-border-radius-default, 0.25em);
          grid-area: button;
        }

        :host([is-open]) .btn-wrapper {
          background-color: var(--cc-color-bg-default, #fff);
          border-radius: var(--cc-border-radius-default, 0.25em) var(--cc-border-radius-default, 0.25em) 0 0;
        }

        .button {
          align-items: center;
          background-color: transparent;
          border: solid 1px transparent;
          border-radius: var(--cc-border-radius-default, 0.25em);
          display: flex;
          font-family: inherit;
          font-size: 1em;
          padding: 0.25em 0.6em 0.35em;
          position: relative;
          z-index: 1;
        }

        .button:hover {
          border: solid 1px var(--cc-color-border-neutral-strong, #8c8c8c);
        }

        .button:focus-visible {
          outline: var(--cc-focus-outline, #000 solid 2px);
          outline-offset: var(--cc-focus-outline-offset, 2px);
        }

        .button cc-icon {
          margin-left: 0.1em;
          transition: all 0.3s;
        }

        .button[aria-expanded='false'] cc-icon {
          transform: rotate(180deg);
        }

        .button:hover cc-icon {
          transform: rotate(90deg);
        }

        .content {
          background-color: var(--cc-color-bg-default, #fff);
          border-radius: var(--cc-border-radius-default, 0.25em);
          box-sizing: border-box;
          display: none;
          grid-area: content;
          padding: 1em;
          width: 100%;
        }

        :host([is-open]) .content {
          border-top-left-radius: 0;
          display: block;
        }

        ::slotted([slot='content']) {
          display: flex;
          flex-direction: column;
        }

        .links {
          align-items: center;
          display: flex;
          gap: 0.5em;
          grid-area: links;
          justify-self: end;
        }

        @container (max-width: 34em) {
          .links {
            justify-self: unset;
            margin-block-start: 0.5em;
          }
        }

        ::slotted([slot='link']) {
          align-items: center;
          color: var(--cc-color-text-primary-highlight, blue);
          display: flex;
          flex-direction: row;
          gap: 0.5em;
        }
      `,
    ];
  }
}

window.customElements.define('cc-block-details', CcBlockDetails);
